import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Écran de choix "Particulier / Professionnel" affiché à l'arrivée sur le site.
 *
 * Comportement (calqué sur capeo.eco) :
 *  - plein écran, deux volets photo, animation "portes d'ascenseur" au clic ;
 *  - chaque volet a deux ambiances (temps pluvieux au repos, ciel bleu au survol / au clic) ;
 *  - sur mobile (pas de survol) les deux ambiances alternent en fondu lent ;
 *  - le choix ne survit qu'à la navigation interne : rechargement, nouvel
 *    onglet ou arrivée depuis un site externe ⇒ l'écran réapparaît ;
 *  - `?audience=reset` force la réouverture, `?audience=pro|particulier`
 *    permet de shunter l'écran depuis une campagne.
 */

const KEY = "em_audience";
const ANIM_MS = 2500; // le logo joue sa sortie, les volets ne s'ouvrent qu'ensuite
const CYCLE_MS = 5000; // alternance pluie/soleil sur mobile

const PANELS = [
    {
        id: "particulier",
        side: "left",
        to: "/particuliers",
        image: "/img/gate-particulier.webp",
        imageHover: "/img/gate-particulier-soleil.webp",
        eyebrow: "Energy Market pour les particuliers",
        title: "Particulier",
        text: "Comparez les offres, changez de fournisseur et reprenez la main sur votre facture d'énergie.",
        cta: "Je suis un particulier",
    },
    {
        id: "pro",
        side: "right",
        to: "/professionnels",
        image: "/img/gate-pro.webp",
        imageHover: "/img/gate-pro-soleil.webp",
        eyebrow: "Energy Market pour les professionnels",
        title: "Professionnel",
        text: "Optimisez vos contrats, pilotez vos sites et réduisez durablement vos coûts d'énergie.",
        cta: "Je suis un professionnel",
    },
];

// --- bootstrap : exécuté une seule fois, avant le premier rendu ---
let booted = false;
let forced = null;

function boot() {
    if (booted || typeof window === "undefined") return;
    booted = true;
    try {
        const params = new URLSearchParams(window.location.search);
        const asked = params.get("audience");

        if (asked === "reset") {
            sessionStorage.removeItem(KEY);
            return;
        }
        if (asked === "pro" || asked === "particulier") {
            forced = asked;
            sessionStorage.setItem(KEY, asked);
            document.documentElement.setAttribute("data-audience", asked);
            return;
        }

        // Le profil ne survit pas à un rechargement ni à une arrivée externe.
        let navType = "";
        try {
            const nav = performance.getEntriesByType("navigation")[0];
            navType = nav ? nav.type : "";
        } catch (e) { /* noop */ }

        let internal = false;
        try {
            internal = !!document.referrer && new URL(document.referrer).origin === window.location.origin;
        } catch (e) { /* noop */ }

        if (navType === "reload" || !internal) sessionStorage.removeItem(KEY);

        // Un rechargement, quelle que soit la page, ramène à l'écran de choix.
        // On ne le fait pas pour une arrivée externe : un visiteur venant d'une
        // recherche doit atterrir sur la page qu'il a demandée.
        // location.replace produit une navigation de type « navigate » : pas de
        // boucle possible, et l'URL rechargée ne reste pas dans l'historique.
        if (navType === "reload" && window.location.pathname !== "/") {
            window.location.replace("/");
            return;
        }

        const saved = sessionStorage.getItem(KEY);
        if (saved) document.documentElement.setAttribute("data-audience", saved);
    } catch (e) { /* sessionStorage indisponible : on affichera l'écran */ }
}

boot();

export default function AudienceGate() {
    const navigate = useNavigate();

    const [open, setOpen] = useState(() => {
        if (typeof window === "undefined") return false;
        if (window.location.pathname !== "/") return false;
        if (forced) return false;
        try {
            return !sessionStorage.getItem(KEY);
        } catch (e) {
            return true;
        }
    });
    const [leaving, setLeaving] = useState(false);
    const [chosen, setChosen] = useState(null);
    const [cycleAlt, setCycleAlt] = useState(false);
    const timer = useRef(null);

    // Précharge les quatre visuels dès le montage pour éviter le flash.
    useEffect(() => {
        if (!open) return undefined;
        PANELS.forEach((p) => {
            [p.image, p.imageHover].forEach((src) => {
                const img = new Image();
                img.src = src;
            });
        });
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Sans survol (mobile / tablette empilée) : alternance lente jour ↔ soir.
    useEffect(() => {
        if (!open || typeof window === "undefined" || !window.matchMedia) return undefined;
        const stacked = window.matchMedia("(max-width: 900px)");
        const calm = window.matchMedia("(prefers-reduced-motion: reduce)");
        let id = null;

        const sync = () => {
            if (id) { clearInterval(id); id = null; }
            setCycleAlt(false);
            if (stacked.matches && !calm.matches) {
                id = setInterval(() => setCycleAlt((v) => !v), CYCLE_MS);
            }
        };

        sync();
        stacked.addEventListener("change", sync);
        calm.addEventListener("change", sync);
        return () => {
            if (id) clearInterval(id);
            stacked.removeEventListener("change", sync);
            calm.removeEventListener("change", sync);
        };
    }, [open]);

    useEffect(() => () => clearTimeout(timer.current), []);

    const close = useCallback(
        (audience) => {
            if (leaving) return;
            if (audience) {
                setChosen(audience);
                try {
                    sessionStorage.setItem(KEY, audience);
                } catch (e) { /* noop */ }
                document.documentElement.setAttribute("data-audience", audience);
            }
            setLeaving(true);
            if (audience) {
                const panel = PANELS.find((p) => p.id === audience);
                if (panel) navigate(panel.to);
            }
            timer.current = setTimeout(() => setOpen(false), ANIM_MS);
        },
        [leaving, navigate]
    );

    // Échap : on referme sans choisir (on reste sur l'accueil).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") close(null);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, close]);

    if (!open) return null;

    return (
        <div
            className={`em-gate${leaving ? " is-leaving" : ""}${cycleAlt ? " is-alt" : ""}`}
            role="dialog"
            aria-modal="true"
            aria-label="Vous êtes particulier ou professionnel ?"
            data-testid="audience-gate"
        >
            {PANELS.map((p) => (
                <button
                    key={p.id}
                    type="button"
                    onClick={() => close(p.id)}
                    data-testid={`audience-gate-${p.id}`}
                    className={`em-gate__panel em-gate__panel--${p.side}${chosen === p.id ? " is-alt" : ""}`}
                >
                    <span
                        className="em-gate__layer em-gate__layer--rest"
                        style={{ backgroundImage: `url('${p.image}')` }}
                        aria-hidden="true"
                    />
                    <span
                        className="em-gate__layer em-gate__layer--hover"
                        style={{ backgroundImage: `url('${p.imageHover}')` }}
                        aria-hidden="true"
                    />
                    <span className="em-gate__overlay" aria-hidden="true" />
                    <span className="em-gate__content">
                        <span className="em-gate__eyebrow">{p.eyebrow}</span>
                        <span className="em-gate__title font-display">{p.title}</span>
                        <span className="em-gate__text">{p.text}</span>
                        <span className="em-gate__cta">
                            {p.cta}
                            <span aria-hidden="true">→</span>
                        </span>
                    </span>
                </button>
            ))}

            {/* Logo en deux calques : le mot est aspiré vers la gauche par la
                feuille, qui pivote ensuite dans le sens horaire avant de
                disparaître (animation jouée à l'ouverture des volets). */}
            <div className="em-gate__logo" aria-hidden="true">
                <img className="em-gate__mark" src="/img/logo-em-mark.png" alt="" />
                <img className="em-gate__word" src="/img/logo-em-word.png" alt="" />
            </div>
        </div>
    );
}
